import { normalizeMachineIdent } from "./dashboardMapUtils";

export function operatorTelemetryReleaseKey(operatorId) {
  if (operatorId == null || operatorId === "") return "";
  return `operator:${operatorId}`;
}

export function machineTelemetryReleaseKey(ident) {
  const normalized = normalizeMachineIdent(ident);
  return normalized ? `machine:${normalized}` : "";
}

export function readReleaseEntry(value) {
  if (!value) return null;
  if (typeof value === "string") return { at: value, reason: "end" };
  if (typeof value === "object" && value.at) {
    return { at: value.at, reason: value.reason || "end" };
  }
  return null;
}

export function releaseFieldsFromEntry(value) {
  const entry = readReleaseEntry(value);
  if (!entry) {
    return { telemetryReleasedAt: null, telemetryPausedAt: null };
  }
  if (entry.reason === "pause") {
    return { telemetryReleasedAt: null, telemetryPausedAt: entry.at };
  }
  return { telemetryReleasedAt: entry.at, telemetryPausedAt: null };
}

export function telemetryReleasesFromApi(releases) {
  const map = {};
  (releases || []).forEach((item) => {
    if (!item || !item.releasedAt) return;
    const entry = {
      at: item.releasedAt,
      reason: item.reason || "end",
    };
    const lastMachine = String(item.lastMachineIdent || "").trim();
    if (lastMachine) entry.iconMachineIdent = lastMachine;
    const operatorKey = operatorTelemetryReleaseKey(item.operatorId);
    if (operatorKey && item.bindOperator !== false) map[operatorKey] = entry;
    const machineKey = machineTelemetryReleaseKey(item.machineIdent);
    if (machineKey) map[machineKey] = entry;
  });
  return map;
}

/** Telemetria aberta sem máquina nova mantém o vínculo visual com a última máquina. */
export function applySessionLastMachine(operators, sessions) {
  const next = { ...(operators || {}) };
  (sessions || []).forEach((session) => {
    if (!session || normalizeMachineIdent(session.machineIdent)) return;
    const ident = String(session.lastMachineIdent || "").trim();
    if (!ident || session.operatorId == null || session.operatorId === "") return;
    const operator = next[session.operatorId];
    if (!operator || operator.assumedMachineIdent) return;
    next[session.operatorId] = {
      ...operator,
      iconMachineIdent: ident,
    };
  });
  return next;
}

/** Operador sem sessão aberta continua ao lado da última máquina que assumiu. */
export function applyIconMachinePairing(operators, releases) {
  const next = { ...(operators || {}) };
  Object.values(next).forEach((operator) => {
    if (!operator || operator.assumedMachineIdent) return;
    const entry = releases && releases[operatorTelemetryReleaseKey(operator.id)];
    const ident = entry && entry.iconMachineIdent;
    if (!ident) return;
    next[operator.id] = {
      ...operator,
      iconMachineIdent: String(ident),
    };
  });
  return next;
}

export function mergeTelemetryReleases(memoryReleases, apiReleases) {
  const merged = { ...(memoryReleases || {}) };
  Object.entries(apiReleases || {}).forEach(([key, incomingRaw]) => {
    const incoming = readReleaseEntry(incomingRaw);
    if (!incoming) return;
    const existing = readReleaseEntry(merged[key]);
    if (
      key.startsWith("machine:") &&
      existing &&
      existing.at &&
      incoming.at
    ) {
      const existingMs = Date.parse(existing.at);
      const incomingMs = Date.parse(incoming.at);
      if (
        Number.isFinite(existingMs) &&
        Number.isFinite(incomingMs) &&
        incomingMs > existingMs
      ) {
        return;
      }
    }
    merged[key] = incomingRaw;
  });
  return merged;
}

export function nextTelemetryReleases({
  previousSessions,
  nextSessions,
  previousReleases = {},
  now = new Date(),
} = {}) {
  const previous = Array.isArray(previousSessions) ? previousSessions : [];
  const next = Array.isArray(nextSessions) ? nextSessions : [];
  const nextOperatorIds = new Set();
  const nextMachines = new Set();
  next.forEach((session) => {
    if (!session) return;
    if (session.operatorId != null && session.operatorId !== "") {
      nextOperatorIds.add(session.operatorId);
    }
    const machine = normalizeMachineIdent(session.machineIdent);
    if (machine) nextMachines.add(machine);
  });

  const releases = { ...previousReleases };
  const stamp = now instanceof Date ? now.toISOString() : String(now);

  previous.forEach((session) => {
    if (!session) return;
    const operatorKey = operatorTelemetryReleaseKey(session.operatorId);
    if (
      operatorKey &&
      !nextOperatorIds.has(session.operatorId) &&
      !releases[operatorKey]
    ) {
      releases[operatorKey] = stamp;
    }
    const machineKey = machineTelemetryReleaseKey(session.machineIdent);
    if (
      machineKey &&
      !nextMachines.has(normalizeMachineIdent(session.machineIdent)) &&
      !releases[machineKey]
    ) {
      releases[machineKey] = stamp;
    }
  });

  next.forEach((session) => {
    if (!session) return;
    const operatorKey = operatorTelemetryReleaseKey(session.operatorId);
    if (operatorKey) delete releases[operatorKey];
    const machineKey = machineTelemetryReleaseKey(session.machineIdent);
    if (machineKey) delete releases[machineKey];
  });

  return releases;
}

/** Quem encerrou ou pausou, fora da lista de telemetria aberta. Um nome por operador. */
export function inactiveTelemetryOperators(sessions, releases) {
  const activeIds = new Set(
    (sessions || [])
      .filter(
        (session) =>
          session && session.operatorId != null && session.operatorId !== ""
      )
      .map((session) => String(session.operatorId))
  );
  const byOperator = new Map();
  (releases || []).forEach((item) => {
    if (!item || item.bindOperator === false) return;
    if (item.operatorId == null || item.operatorId === "") return;
    const key = String(item.operatorId);
    if (activeIds.has(key) || !item.releasedAt) return;
    const current = byOperator.get(key);
    if (!current || String(item.releasedAt) > String(current.releasedAt)) {
      byOperator.set(key, item);
    }
  });
  return Array.from(byOperator.values()).sort((left, right) =>
    String(right.releasedAt).localeCompare(String(left.releasedAt))
  );
}

export function telemetrySessionKey(session) {
  if (!session) return "";
  if (session.sessionUuid) return `uuid:${session.sessionUuid}`;
  return `op:${session.operatorId}:${session.assumedAt}:${session.machineIdent || ""}`;
}

export function nextTelemetryPopup(previousKeys, sessions, { popupOpen } = {}) {
  const list = Array.isArray(sessions) ? sessions : [];
  const currentKeys = list.map(telemetrySessionKey);
  if (previousKeys == null) {
    return { popup: null, keys: new Set(currentKeys) };
  }
  if (popupOpen) {
    return { popup: null, keys: previousKeys };
  }
  const unseen = list.filter(
    (item) => !previousKeys.has(telemetrySessionKey(item))
  );
  const keys = new Set(previousKeys);
  currentKeys.forEach((key) => keys.add(key));
  return { popup: unseen[0] || null, keys };
}

export function markTelemetrySessionsSeen(previousKeys, sessions) {
  const keys = new Set(previousKeys || []);
  (sessions || []).forEach((item) => keys.add(telemetrySessionKey(item)));
  return keys;
}

export function telemetryClockLabel(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function assumeRelativeParts(assumedAt, now = new Date()) {
  const then = new Date(assumedAt);
  if (Number.isNaN(then.getTime())) {
    return { unit: "now", count: 0 };
  }
  const diffMs = Math.max(0, now.getTime() - then.getTime());
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return { unit: "now", count: 0 };
  if (minutes < 60) return { unit: "minutes", count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: "hours", count: hours };
  return { unit: "days", count: Math.floor(hours / 24) };
}

export function dayKeyFromAssumedAt(assumedAt) {
  if (!assumedAt) return "";
  const date = new Date(assumedAt);
  if (Number.isNaN(date.getTime())) {
    return String(assumedAt).slice(0, 10);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function buildFieldAppTrackingHref(farmId, session) {
  if (!farmId || !session) return "";
  const params = new URLSearchParams();
  params.set("tab", "tracking");
  if (session.operatorId) params.set("operator", String(session.operatorId));
  if (session.machineIdent) params.set("machine", session.machineIdent);
  const day = dayKeyFromAssumedAt(session.assumedAt);
  if (day) params.set("day", day);
  if (session.sessionUuid) params.set("session", session.sessionUuid);
  return `/${farmId}/field-app/?${params.toString()}`;
}
